import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Phone, Bot, X, Send } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { ShoppingCart } from 'lucide-react';

//-- Proyecto diseñado, programado, testeado, ejecutado y publicado por Alex Castillo.
//-- Autor: Alex Castillo © 2026 Alex Castillo – Todos los derechos reservados.

const Chatbot = () => {
  const { isChatbotOpen, setIsChatbotOpen, t } = useApp();
  const [messages, setMessages] = useState([
    { id: 1, text: t.chatbot.welcome, sender: 'bot' }
  ]);
  const [inputText, setInputText] = useState('');

  const quickOptions = t.chatbot.options;

  const handleSendMessage = (text) => {
    if (!text.trim()) return;

    const newMessage = {
      id: Date.now(),
      text: text,
      sender: 'user'
    };

    setMessages(prev => [...prev, newMessage]);

    setTimeout(() => {
      let botResponse = "Gracias por tu mensaje. Un asesor se comunicará contigo pronto.";
      
      if (text.toLowerCase().includes('catálogo','catalogo')) {
        botResponse = "Te invito a explorar nuestro catálogo con las mejores opciones en equipos eléctricos especializados.";
      } else if (text.toLowerCase().includes('producto','productos')) {
        botResponse = "Contamos con epps dieléctricos, pértigas, dispositivos de load break, arnés de seguridad, herramientas aisladas y mucho más. ¿Qué necesitas específicamente?";
      } else if (text.toLowerCase().includes('contacto', 'contactar') || text.toLowerCase().includes('soporte')) {
        botResponse = "Puedes contactarnos al WhatsApp o visitar nuestra sección de contacto para más información.";
      } else if (text.toLowerCase().includes('empresa')) {
      botResponse = "Nuestro equipo se ha hecho participe desde el año 2018 en actividades referentes a distribución de elementos de protección personal enfocados a trabajos con riesgo eléctrico y adyacentes, garantizando siempre la más alta calidad.";
      }

      setMessages(prev => [...prev, {
        id: Date.now(),
        text: botResponse,
        sender: 'bot'
      }]);
    }, 1000);

    setInputText('');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleSendMessage(inputText);
  };

  const { cart, setIsCartOpen } = useApp();

  const cartItemCount = cart.reduce((total, item) => total + item.quantity, 0);

  return (
    <>
      <motion.button
        onClick={() => setIsCartOpen(true)}
        className="fixed bottom-40 right-6 bg-gradient-to-r from-yellow-500 to-yellow-800 text-white p-4 rounded-full shadow-xl shadow-gray-700 hover:shadow-2xl z-50"
        whileHover={{ scale: 1.15 }}
        whileTap={{ scale: 0.95 }}
      >
        <ShoppingCart className="w-6 h-6" />

        {cartItemCount > 0 && (
          <span className="absolute -top-2 -right-2 bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold">
            {cartItemCount}
          </span>
        )}
      </motion.button>
      
      <motion.button
        onClick={() => setIsChatbotOpen(true)}
        className="fixed bottom-24 right-6 bg-gradient-to-r from-blue-500 to-blue-800 text-white p-4 rounded-full shadow-xl shadow-gray-700 hover:shadow-2xl z-50"
        whileHover={{ scale: 1.15 }}
        whileTap={{ scale: 0.95 }}
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 0 }}
      >
        <Bot className="w-6 h-6" />
      </motion.button>

      <motion.div
        onClick={() => window.open('https://wa.me/573174295659', '_blank')}
        className="fixed bottom-8 right-6 bg-gradient-to-r from-green-500 to-green-800 text-white p-4 rounded-full shadow-xl shadow-gray-700 hover:shadow-2xl z-40 cursor-pointer"
        whileHover={{ scale: 1.15 }}
        whileTap={{ scale: 0.95 }}
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 0 }}
      >
        <Phone className="w-6 h-6"/>
      </motion.div>

      <AnimatePresence>
        {isChatbotOpen && (
          <motion.div
            className="fixed bottom-24 right-6 w-80 bg-gradient-to-r from-white to-gray-300 rounded-2xl shadow-2xl z-50 overflow-hidden"
            initial={{ opacity: 0, y: 20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.9 }}
          >
            <div className="bg-gradient-to-r from-blue-500 to-blue-800 text-white p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                  <span>🤖</span>
                </div>
                <span className="font-semibold">Asistente Virtual JGW</span>
              </div>
              <button
                onClick={() => setIsChatbotOpen(false)}
                className="p-1 hover:bg-white/20 rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="h-64 overflow-y-auto p-4 space-y-3">
              {messages.map((message) => (
                <motion.div
                  key={message.id}
                  className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <div className={`max-w-xs p-3 rounded-lg ${
                    message.sender === 'user'
                      ? 'bg-gradient-to-r from-blue-500 to-blue-800 text-white shadow-lg shadow-gray-700'
                      : 'bg-gradient-to-t from-gray-100 to-white text-gray-800 shadow-lg shadow-gray-700'
                  }`}>
                    {message.text}
                  </div>
                </motion.div>
              ))}
            </div>

            <div className="border-t p-3">
              <div className="grid grid-cols-2 gap-2 mb-3">
                {quickOptions.map((option, index) => (
                  <button
                    key={index}
                    onClick={() => handleSendMessage(option)}
                    className="text-xs bg-gray-500 hover:bg-gray-800 p-2 rounded-lg text-white shadow-lg shadow-gray-700"
                  >
                    {option}
                  </button>
                ))}
              </div>

              <form onSubmit={handleSubmit} className="flex gap-2">
                <input
                  type="text"
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  placeholder="Escribe tu mensaje..."
                  className="flex-1 p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                />
                <motion.button
                  type="submit"
                  className="bg-gradient-to-r from-blue-500 to-blue-800 text-white p-2 rounded-lg"
                  whileHover={{ scale: 1.2 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Send className="w-4 h-4" />
                </motion.button>
              </form>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Chatbot;

//-- Proyecto diseñado, programado, testeado, ejecutado y publicado por Alex Castillo.
//-- Autor: Alex Castillo © 2026 Alex Castillo – Todos los derechos reservados.